import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, memo } from "react";
import { Box, Typography, Button, IconButton, Chip, Collapse, Select, MenuItem, TextField, FormControl, InputLabel, Checkbox, FormControlLabel, } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FaChevronDown, FaChevronRight, FaCheckCircle, FaCircle, FaTrash, FaPlus, } from "react-icons/fa";
const Container = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isGenerating" && prop !== "isLocked",
})(({ theme, isGenerating, isLocked }) => ({
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: theme.spacing(3),
    pointerEvents: isGenerating || isLocked ? "none" : "auto",
    opacity: isGenerating || isLocked ? 0.7 : 1,
    transition: "opacity 0.2s ease",
    fontFamily: "IBM Plex Sans, sans-serif",
}));
const SubjectSection = styled(Box)(({ theme }) => ({
    border: "1px solid #D5DDE4",
    borderRadius: "12px",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    transition: "all 0.2s ease",
    "&:hover": {
        boxShadow: "0 2px 8px rgba(14, 73, 122, 0.1)",
    },
}));
const SubjectHeader = styled(Box)(({ theme, selected }) => ({
    backgroundColor: selected ? "#0E497A" : "#F5F7FA",
    color: selected ? "white" : "#0E497A",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.2s ease",
}));
const TopicSection = styled(Box)(({ theme }) => ({
    backgroundColor: "#FAFBFC",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: "8px",
    border: "1px solid #E8EEF3",
}));
const TopicHeader = styled(Box)(({ theme, selected }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: selected ? "2px solid #0E497A" : "1px solid #E8EEF3",
}));
const SubtopicCard = styled(Box)(({ theme, selected }) => ({
    backgroundColor: "white",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: "8px",
    border: selected ? "2px solid #0E497A" : "1px solid #E8EEF3",
    transition: "all 0.2s ease",
    "&:hover": {
        borderColor: "#0E497A",
        boxShadow: "0 2px 4px rgba(14, 73, 122, 0.08)",
    },
}));
const SubtopicHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
}));
const RuleContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#F8FAFC",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid #E5E9F2",
    position: "relative",
}));
const ConfigGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
    alignItems: "center",
}));
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        fontSize: "14px",
        backgroundColor: "white",
        "& fieldset": {
            borderColor: "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
    },
    "& .MuiInputLabel-root": {
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
        "&.Mui-focused": {
            color: "#0E497A",
        },
    },
    "& .MuiSelect-select": {
        fontFamily: "IBM Plex Sans, sans-serif",
    },
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        fontSize: "14px",
        backgroundColor: "white",
        "& fieldset": {
            borderColor: "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
    },
    "& .MuiInputLabel-root": {
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
        "&.Mui-focused": {
            color: "#0E497A",
        },
    },
    "& .MuiInputBase-input": {
        fontFamily: "IBM Plex Sans, sans-serif",
    },
}));
const SubmitButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#0E497A",
    color: "white",
    fontFamily: "IBM Plex Sans, sans-serif",
    borderRadius: "8px",
    padding: "12px 32px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "15px",
    "&:hover": {
        backgroundColor: "#0A3A5E",
    },
    "&.Mui-disabled": {
        backgroundColor: "#E0E0E0",
        color: "#9E9E9E",
    },
}));
const SummaryChip = styled(Chip)(({ theme }) => ({
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "12px",
    height: "24px",
}));
const AddRuleButton = styled(Button)(({ theme }) => ({
    color: "#0E497A",
    textTransform: "none",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    padding: "4px 8px",
    "&:hover": {
        backgroundColor: "rgba(14, 73, 122, 0.05)",
    },
}));
const RuleItem = memo(({ rule, idx, isSingleRule, subject, topic, subtopic, onRuleChange, onRemoveRule }) => {
    return (_jsxs(RuleContainer, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }, children: [_jsx(Typography, { variant: "caption", sx: { color: '#5F6368', fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif' } }), !isSingleRule && (_jsx(IconButton, { size: "small", onClick: () => onRemoveRule(subject, topic, subtopic, rule.id), sx: { color: '#9E9E9E', '&:hover': { color: '#F44336' } }, children: _jsx(FaTrash, { size: 12 }) }))] }), _jsxs(ConfigGrid, { children: [_jsxs(StyledFormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Difficulty" }), _jsxs(Select, { MenuProps: {
                                    disablePortal: true,
                                    onMouseDown: (e) => e.stopPropagation(),
                                    sx: { "& .MuiMenuItem-root": { fontFamily: "IBM Plex Sans, sans-serif", fontSize: "14px" } }
                                }, onClick: (e) => e.stopPropagation(), value: rule.difficulty, label: "Difficulty", onChange: (e) => onRuleChange(subject, topic, subtopic, rule.id, "difficulty", e.target.value), children: [_jsx(MenuItem, { value: "Easy", children: "Easy" }), _jsx(MenuItem, { value: "Moderate", children: "Moderate" }), _jsx(MenuItem, { value: "Hard", children: "Hard" })] })] }), _jsxs(StyledFormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Bloom's Taxonomy" }), _jsxs(Select, { MenuProps: {
                                    disablePortal: true,
                                    onMouseDown: (e) => e.stopPropagation(),
                                    sx: { "& .MuiMenuItem-root": { fontFamily: "IBM Plex Sans, sans-serif", fontSize: "14px" } }
                                }, onClick: (e) => e.stopPropagation(), value: rule.taxonomy, label: "Bloom's Taxonomy", onChange: (e) => onRuleChange(subject, topic, subtopic, rule.id, "taxonomy", e.target.value), children: [_jsx(MenuItem, { value: "Remember", children: "Remember" }), _jsx(MenuItem, { value: "Understand", children: "Understand" }), _jsx(MenuItem, { value: "Apply", children: "Apply" }), _jsx(MenuItem, { value: "Analyse", children: "Analyse" }), _jsx(MenuItem, { value: "Evaluate", children: "Evaluate" }), _jsx(MenuItem, { value: "Create", children: "Create" })] })] }), _jsxs(StyledFormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Question Type" }), _jsxs(Select, { MenuProps: {
                                    disablePortal: true,
                                    onMouseDown: (e) => e.stopPropagation(),
                                    sx: { "& .MuiMenuItem-root": { fontFamily: "IBM Plex Sans, sans-serif", fontSize: "14px" } }
                                }, onClick: (e) => e.stopPropagation(), value: rule.itemType, label: "Question Type", onChange: (e) => onRuleChange(subject, topic, subtopic, rule.id, "itemType", e.target.value), children: [_jsx(MenuItem, { value: "MCQ", children: "MCQ Single Correct" }), _jsx(MenuItem, { value: "MCQ Multiple Correct", children: "MCQ Multiple Correct" }), _jsx(MenuItem, { value: "True/False", children: "True/False" }), _jsx(MenuItem, { value: "Comprehension", children: "Comprehension" }), _jsx(MenuItem, { value: "Match", children: "Match" })] })] }), _jsx(StyledTextField, { onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), size: "small", fullWidth: true, type: "number", label: "Number of questions", value: rule.numberOfQuestions, onChange: (e) => onRuleChange(subject, topic, subtopic, rule.id, "numberOfQuestions", parseInt(e.target.value) || 0), inputProps: { min: 1, max: 50 } }), (rule.itemType === "MCQ" || rule.itemType === "MCQ Multiple Correct") && (_jsx(StyledTextField, { onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), size: "small", fullWidth: true, type: "number", label: "Number of options", value: rule.numberOfDistractors, onChange: (e) => onRuleChange(subject, topic, subtopic, rule.id, "numberOfDistractors", parseInt(e.target.value) || 0), inputProps: { min: 3, max: 5 } }))] })] }));
});
const SubtopicRow = memo(({ subject, topic, subtopic, config, onSubtopicSelect, onRuleChange, onAddRule, onRemoveRule }) => {
    const isSelected = config?.selected;
    return (_jsxs(SubtopicCard, { selected: isSelected, children: [_jsxs(SubtopicHeader, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(IconButton, { size: "small", onClick: () => onSubtopicSelect(subject, topic, subtopic, !isSelected), sx: { p: 0 }, children: isSelected ? (_jsx(FaCheckCircle, { size: 20, color: "#0E497A" })) : (_jsx(FaCircle, { size: 20, color: "#D5DDE4" })) }), _jsx(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    color: isSelected ? "#0E497A" : "#5F6368",
                                }, children: subtopic })] }), isSelected && (_jsx(Box, { sx: { display: 'flex', gap: 1 }, children: config.rules.map((rule) => (_jsx(Chip, { label: `${rule.numberOfQuestions} ${rule.difficulty}`, size: "small", sx: {
                                backgroundColor: rule.difficulty === 'Easy' ? '#E8F5E9' : rule.difficulty === 'Hard' ? '#FFEBEE' : '#E8F4FD',
                                color: rule.difficulty === 'Easy' ? '#2E7D32' : rule.difficulty === 'Hard' ? '#C62828' : '#0E497A',
                                fontFamily: "IBM Plex Sans, sans-serif",
                                fontSize: "12px",
                                fontWeight: 500,
                                '& .MuiChip-label': {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                },
                            } }, rule.id))) }))] }), _jsx(Collapse, { in: isSelected, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [config?.rules.map((rule, idx) => (_jsx(RuleItem, { rule: rule, idx: idx, isSingleRule: config.rules.length === 1, subject: subject, topic: topic, subtopic: subtopic, onRuleChange: onRuleChange, onRemoveRule: onRemoveRule }, rule.id))), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start', mt: 1 }, children: _jsx(AddRuleButton, { startIcon: _jsx(FaPlus, { size: 12 }), onClick: () => onAddRule(subject, topic, subtopic) }) })] }) })] }));
});
const BlueprintGenerator = ({ tags, onSubmit, isGenerating, isLocked }) => {
    const [expandedSubjects, setExpandedSubjects] = useState({});
    const [expandedTopics, setExpandedTopics] = useState({});
    // Initialize configuration state
    const initializeConfig = () => {
        const config = {};
        tags.subject_tags.forEach((tag) => {
            const topicsConfig = {};
            tag.topic_tags.forEach((topic) => {
                const subtopicsConfig = {};
                topic.subtopic_tags.forEach((subtopic) => {
                    subtopicsConfig[subtopic] = {
                        selected: false,
                        rules: [
                            {
                                id: crypto.randomUUID(),
                                difficulty: "Moderate",
                                taxonomy: "Apply",
                                itemType: "MCQ",
                                numberOfQuestions: 5,
                                numberOfDistractors: 4
                            }
                        ]
                    };
                });
                const hasSubtopics = Object.keys(subtopicsConfig).length > 0;
                topicsConfig[topic.topic] = {
                    selected: false,
                    subtopics: subtopicsConfig,
                    rules: !hasSubtopics ? [
                        {
                            id: crypto.randomUUID(),
                            difficulty: "Moderate",
                            taxonomy: "Apply",
                            itemType: "MCQ",
                            numberOfQuestions: 5,
                            numberOfDistractors: 4
                        }
                    ] : undefined
                };
            });
            config[tag.subject] = {
                selected: false,
                topics: topicsConfig,
            };
        });
        return config;
    };
    const [config, setConfig] = useState(initializeConfig());
    const toggleSubject = useCallback((subject) => {
        setExpandedSubjects((prev) => ({
            ...prev,
            [subject]: !prev[subject],
        }));
    }, []);
    const toggleTopic = useCallback((subject, topic) => {
        setExpandedTopics((prev) => ({
            ...prev,
            [`${subject}-${topic}`]: !prev[`${subject}-${topic}`],
        }));
    }, []);
    const handleSubjectSelect = useCallback((subject, selected) => {
        setConfig((prev) => {
            const updatedTopics = Object.entries(prev[subject].topics).reduce((acc, [topicKey, topicConfig]) => {
                const updatedSubtopics = Object.entries(topicConfig.subtopics).reduce((subAcc, [subtopicKey, subtopicConfig]) => {
                    subAcc[subtopicKey] = { ...subtopicConfig, selected };
                    return subAcc;
                }, {});
                acc[topicKey] = {
                    ...topicConfig,
                    selected,
                    subtopics: updatedSubtopics,
                };
                return acc;
            }, {});
            return {
                ...prev,
                [subject]: {
                    ...prev[subject],
                    selected,
                    topics: updatedTopics,
                },
            };
        });
    }, []);
    const handleTopicSelect = useCallback((subject, topic, selected) => {
        setConfig((prev) => {
            const subjectConfig = prev[subject];
            const topicConfig = subjectConfig.topics[topic];
            // Update subtopics
            const updatedSubtopics = Object.entries(topicConfig.subtopics).reduce((acc, [key, val]) => {
                acc[key] = { ...val, selected };
                return acc;
            }, {});
            const updatedTopics = {
                ...subjectConfig.topics,
                [topic]: {
                    ...topicConfig,
                    selected,
                    subtopics: updatedSubtopics,
                },
            };
            // Calculate if subject should be selected (if all topics are selected)
            const allTopicsSelected = Object.values(updatedTopics).every(t => t.selected);
            return {
                ...prev,
                [subject]: {
                    ...subjectConfig,
                    selected: allTopicsSelected,
                    topics: updatedTopics,
                },
            };
        });
    }, []);
    const handleTopicRuleChange = useCallback((subject, topic, ruleId, field, value) => {
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        rules: prev[subject].topics[topic].rules?.map(r => r.id === ruleId ? { ...r, [field]: value } : r)
                    }
                }
            }
        }));
    }, []);
    const addTopicRule = useCallback((subject, topic) => {
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        rules: [
                            ...(prev[subject].topics[topic].rules || []),
                            {
                                id: crypto.randomUUID(),
                                difficulty: "Moderate",
                                taxonomy: "Apply",
                                itemType: "MCQ",
                                numberOfQuestions: 5,
                                numberOfDistractors: 4
                            }
                        ]
                    }
                }
            }
        }));
    }, []);
    const removeTopicRule = useCallback((subject, topic, ruleId) => {
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        rules: prev[subject].topics[topic].rules?.filter(r => r.id !== ruleId)
                    }
                }
            }
        }));
    }, []);
    const handleSubtopicSelect = useCallback((subject, topic, subtopic, selected) => {
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        subtopics: {
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: {
                                ...prev[subject].topics[topic].subtopics[subtopic],
                                selected: selected
                            }
                        },
                        // Update topic selected state based on new subtopic state
                        selected: Object.values({
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: {
                                ...prev[subject].topics[topic].subtopics[subtopic],
                                selected: selected
                            }
                        }).every(st => st.selected)
                    }
                },
                // Note: Subject selection logic is simplified here to avoid massive nesting or requires separate pass.
                // For strict correctness we should check all topics, but let's trust the user clicks for now or keep it simple.
                // Or we can recalculate subject selection if we want perfectly synced state.
                // Re-implementing full subject check:
                selected: Object.values({
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        subtopics: {
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: { ...prev[subject].topics[topic].subtopics[subtopic], selected: selected }
                        },
                        selected: Object.values({
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: { ...prev[subject].topics[topic].subtopics[subtopic], selected: selected }
                        }).every(st => st.selected)
                    }
                }).every(t => t.selected)
            }
        }));
    }, []);
    const handleRuleChange = useCallback((subject, topic, subtopic, ruleId, field, value) => {
        if (!subtopic) {
            handleTopicRuleChange(subject, topic, ruleId, field, value);
            return;
        }
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        subtopics: {
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: {
                                ...prev[subject].topics[topic].subtopics[subtopic],
                                rules: prev[subject].topics[topic].subtopics[subtopic].rules.map(r => r.id === ruleId ? { ...r, [field]: value } : r)
                            }
                        }
                    }
                }
            }
        }));
    }, [handleTopicRuleChange]);
    const addRule = useCallback((subject, topic, subtopic) => {
        if (!subtopic) {
            addTopicRule(subject, topic);
            return;
        }
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        subtopics: {
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: {
                                ...prev[subject].topics[topic].subtopics[subtopic],
                                rules: [
                                    ...prev[subject].topics[topic].subtopics[subtopic].rules,
                                    {
                                        id: crypto.randomUUID(),
                                        difficulty: "Moderate",
                                        taxonomy: "Apply",
                                        itemType: "MCQ",
                                        numberOfQuestions: 5,
                                        numberOfDistractors: 4
                                    }
                                ]
                            }
                        }
                    }
                }
            }
        }));
    }, []);
    const removeRule = useCallback((subject, topic, subtopic, ruleId) => {
        if (!subtopic) {
            removeTopicRule(subject, topic, ruleId);
            return;
        }
        setConfig((prev) => ({
            ...prev,
            [subject]: {
                ...prev[subject],
                topics: {
                    ...prev[subject].topics,
                    [topic]: {
                        ...prev[subject].topics[topic],
                        subtopics: {
                            ...prev[subject].topics[topic].subtopics,
                            [subtopic]: {
                                ...prev[subject].topics[topic].subtopics[subtopic],
                                rules: prev[subject].topics[topic].subtopics[subtopic].rules.filter(r => r.id !== ruleId)
                            }
                        }
                    }
                }
            }
        }));
    }, [removeTopicRule]);
    const getSelectionSummary = () => {
        let totalTopics = 0;
        let selectedTopics = 0;
        let totalQuestions = 0;
        Object.entries(config).forEach(([subject, subjectConfig]) => {
            Object.entries(subjectConfig.topics).forEach(([topic, topicConfig]) => {
                totalTopics++;
                if (topicConfig.selected) {
                    selectedTopics++;
                    // Count rules if no subtopics
                    if (Object.keys(topicConfig.subtopics).length === 0 && topicConfig.rules) {
                        topicConfig.rules.forEach((rule) => {
                            totalQuestions += rule.numberOfQuestions;
                        });
                    }
                }
                Object.entries(topicConfig.subtopics).forEach(([subtopic, subtopicConfig]) => {
                    if (subtopicConfig.selected) {
                        subtopicConfig.rules.forEach((rule) => {
                            totalQuestions += rule.numberOfQuestions;
                        });
                    }
                });
            });
        });
        return { totalTopics, selectedTopics, totalQuestions };
    };
    const handleSubmit = () => {
        const summary = getSelectionSummary();
        if (summary.totalQuestions === 0) {
            alert("Please select at least one subtopic to generate questions.");
            return;
        }
        // Flatten the configuration for the backend
        // The backend expects: subtopics: { "Subtopic Name": { difficulty: "...", ... } }
        // We will generate unique keys for each rule: "Subtopic Name [Variant 1]", "Subtopic Name [Variant 2]"
        const flattenedConfig = {};
        Object.entries(config).forEach(([subject, subjectConfig]) => {
            // We process all subjects to find selected subtopics within them
            // Do NOT check subjectConfig.selected here, as it may be false even if some subtopics are selected.
            let subjectEntry = null;
            Object.entries(subjectConfig.topics).forEach(([topic, topicConfig]) => {
                // Similarly, do NOT check topicConfig.selected
                let topicEntry = null;
                // Process Subtopics
                Object.entries(topicConfig.subtopics).forEach(([subtopic, subtopicConfig]) => {
                    if (!subtopicConfig.selected)
                        return;
                    // Initialize parent entries on demand
                    if (!subjectEntry) {
                        if (!flattenedConfig[subject]) {
                            flattenedConfig[subject] = { selected: true, topics: {} };
                        }
                        subjectEntry = flattenedConfig[subject];
                    }
                    if (!topicEntry) {
                        if (!subjectEntry.topics[topic]) {
                            subjectEntry.topics[topic] = { selected: true, subtopics: {} };
                        }
                        topicEntry = subjectEntry.topics[topic];
                    }
                    // Process each rule as a separate "subtopic" entry
                    subtopicConfig.rules.forEach((rule, index) => {
                        const uniqueSubtopicKey = subtopicConfig.rules.length > 1
                            ? `${subtopic} (Variant ${index + 1})`
                            : subtopic;
                        topicEntry.subtopics[uniqueSubtopicKey] = {
                            selected: true,
                            difficulty: rule.difficulty,
                            taxonomy: rule.taxonomy,
                            itemType: rule.itemType,
                            numberOfQuestions: rule.numberOfQuestions,
                            numberOfDistractors: rule.numberOfDistractors,
                            originalSubtopicName: subtopic
                        };
                    });
                });
                // Process Topic-Level Rules (if no subtopics and topic is selected)
                if (Object.keys(topicConfig.subtopics).length === 0 && topicConfig.rules && topicConfig.selected) {
                    // Initialize parent entries on demand
                    if (!subjectEntry) {
                        if (!flattenedConfig[subject]) {
                            flattenedConfig[subject] = { selected: true, topics: {} };
                        }
                        subjectEntry = flattenedConfig[subject];
                    }
                    if (!topicEntry) {
                        if (!subjectEntry.topics[topic]) {
                            subjectEntry.topics[topic] = { selected: true, subtopics: {} };
                        }
                        topicEntry = subjectEntry.topics[topic];
                    }
                    topicConfig.rules.forEach((rule, index) => {
                        const uniqueSubtopicKey = topicConfig.rules.length > 1
                            ? `${topic} (Variant ${index + 1})`
                            : topic;
                        topicEntry.subtopics[uniqueSubtopicKey] = {
                            selected: true,
                            difficulty: rule.difficulty,
                            taxonomy: rule.taxonomy,
                            itemType: rule.itemType,
                            numberOfQuestions: rule.numberOfQuestions,
                            numberOfDistractors: rule.numberOfDistractors,
                            originalSubtopicName: topic // Treat topic as subtopic
                        };
                    });
                }
            });
        });
        console.log("Submitting flattened config:", flattenedConfig);
        onSubmit(flattenedConfig);
    };
    const summary = getSelectionSummary();
    return (_jsxs(Container, { isGenerating: isGenerating, isLocked: isLocked, children: [_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "h5", sx: {
                            fontFamily: "IBM Plex Sans, sans-serif",
                            fontWeight: 700,
                            color: "#0E497A",
                            mb: 1,
                        }, children: "Question Set" }), _jsx(Typography, { variant: "body2", sx: {
                            fontFamily: "IBM Plex Sans, sans-serif",
                            color: "#5F6368",
                            mb: 2,
                        }, children: "Select subjects, topics, and subtopics, then configure question parameters for each selection." }), _jsxs(Box, { sx: { display: "flex", gap: 1, flexWrap: "wrap" }, children: [_jsx(SummaryChip, { label: `${summary.selectedTopics} / ${summary.totalTopics} Topics Selected`, color: summary.selectedTopics > 0 ? "primary" : "default" }), summary.totalQuestions > 0 && (_jsx(SummaryChip, { label: `${summary.totalQuestions} Total Questions`, color: "success" }))] })] }), tags.subject_tags.map((tag) => {
                const isSubjectExpanded = expandedSubjects[tag.subject];
                const isSubjectSelected = config[tag.subject]?.selected;
                return (_jsxs(SubjectSection, { children: [_jsxs(SubjectHeader, { selected: isSubjectSelected, onClick: () => toggleSubject(tag.subject), children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2 }, children: [isSubjectExpanded ? (_jsx(FaChevronDown, { size: 16 })) : (_jsx(FaChevronRight, { size: 16 })), _jsx(Typography, { sx: {
                                                fontFamily: "IBM Plex Sans, sans-serif",
                                                fontWeight: 600,
                                                fontSize: "16px",
                                            }, children: tag.subject })] }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: isSubjectSelected, onChange: (e) => {
                                            e.stopPropagation();
                                            handleSubjectSelect(tag.subject, e.target.checked);
                                        }, sx: {
                                            color: isSubjectSelected ? "white" : "#0E497A",
                                            "&.Mui-checked": {
                                                color: isSubjectSelected ? "white" : "#0E497A",
                                            },
                                        } }), label: "Select All", sx: {
                                        m: 0,
                                        "& .MuiFormControlLabel-label": {
                                            fontFamily: "IBM Plex Sans, sans-serif",
                                            fontSize: "14px",
                                        },
                                    }, onClick: (e) => e.stopPropagation() })] }), _jsx(Collapse, { in: isSubjectExpanded, children: _jsx(Box, { sx: { p: 2 }, children: tag.topic_tags.map((topicTag) => {
                                    const isTopicExpanded = expandedTopics[`${tag.subject}-${topicTag.topic}`];
                                    const isTopicSelected = config[tag.subject]?.topics[topicTag.topic]?.selected;
                                    return (_jsxs(TopicSection, { children: [_jsxs(TopicHeader, { selected: isTopicSelected, children: [_jsxs(Box, { sx: {
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            cursor: "pointer",
                                                        }, onClick: () => toggleTopic(tag.subject, topicTag.topic), children: [isTopicExpanded ? (_jsx(FaChevronDown, { size: 14 })) : (_jsx(FaChevronRight, { size: 14 })), _jsx(Typography, { sx: {
                                                                    fontFamily: "IBM Plex Sans, sans-serif",
                                                                    fontWeight: 600,
                                                                    fontSize: "15px",
                                                                    color: "#0E497A",
                                                                }, children: topicTag.topic })] }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: isTopicSelected, onChange: (e) => {
                                                                e.stopPropagation();
                                                                handleTopicSelect(tag.subject, topicTag.topic, e.target.checked);
                                                            }, sx: {
                                                                color: "#0E497A",
                                                                "&.Mui-checked": {
                                                                    color: "#0E497A",
                                                                },
                                                            } }), label: "Select All", sx: {
                                                            m: 0,
                                                            "& .MuiFormControlLabel-label": {
                                                                fontFamily: "IBM Plex Sans, sans-serif",
                                                                fontSize: "13px",
                                                            },
                                                        }, onClick: (e) => e.stopPropagation() })] }), _jsx(Collapse, { in: isTopicExpanded, children: topicTag.subtopic_tags.length > 0 ? (topicTag.subtopic_tags.map((subtopic) => {
                                                    const subtopicConfig = config[tag.subject]?.topics[topicTag.topic]
                                                        ?.subtopics[subtopic];
                                                    const isSelected = subtopicConfig?.selected;
                                                    return (_jsx(SubtopicRow, { subject: tag.subject, topic: topicTag.topic, subtopic: subtopic, config: subtopicConfig, onSubtopicSelect: handleSubtopicSelect, onRuleChange: handleRuleChange, onAddRule: addRule, onRemoveRule: removeRule }, subtopic));
                                                })) : (
                                                /* If no subtopics, show rules directly for the topic */
                                                config[tag.subject]?.topics[topicTag.topic]?.rules && (_jsxs(Box, { sx: { p: 2, display: 'flex', flexDirection: 'column', gap: 1 }, children: [config[tag.subject]?.topics[topicTag.topic]?.rules?.map((rule, idx) => (_jsx(RuleItem, { rule: rule, idx: idx, isSingleRule: config[tag.subject]?.topics[topicTag.topic]?.rules?.length === 1, subject: tag.subject, topic: topicTag.topic, subtopic: undefined, onRuleChange: (s, t, st, id, f, v) => handleTopicRuleChange(s, t, id, f, v), onRemoveRule: (s, t, st, id) => removeTopicRule(s, t, id) }, rule.id))), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start', mt: 1 }, children: _jsx(AddRuleButton, { startIcon: _jsx(FaPlus, { size: 12 }), onClick: () => addTopicRule(tag.subject, topicTag.topic), children: "Add Verification Rule" }) })] }))) })] }, topicTag.topic));
                                }) }) })] }, tag.subject));
            }), _jsx(Box, { sx: { display: "flex", justifyContent: "flex-end", mt: 3 }, children: _jsx(SubmitButton, { variant: "contained", onClick: handleSubmit, disabled: summary.totalQuestions === 0 || isGenerating || isLocked, children: isGenerating
                        ? "Generating..."
                        : isLocked
                            ? "Questions Generated"
                            : `Generate ${summary.totalQuestions} Questions` }) })] }));
};
// Demo data
// Demo data
const demoTags = {
    subject_tags: [
        {
            subject: "Mathematics",
            topic_tags: [
                {
                    topic: "Algebra",
                    subtopic_tags: [
                        "Linear Equations",
                        "Quadratic Functions",
                        "Polynomial Operations",
                    ],
                },
                {
                    topic: "Geometry",
                    subtopic_tags: [
                        "Triangles",
                        "Circles",
                        "Coordinate Geometry",
                    ],
                },
            ],
        },
        {
            subject: "Physics",
            topic_tags: [
                {
                    topic: "Mechanics",
                    subtopic_tags: [
                        "Newton's Laws",
                        "Work and Energy",
                        "Momentum",
                    ],
                },
                {
                    topic: "Thermodynamics",
                    subtopic_tags: [
                        "Heat Transfer",
                        "Laws of Thermodynamics",
                        "Entropy",
                    ],
                },
            ],
        },
    ],
};
export default BlueprintGenerator;
